import {createColumnHelper} from "@tanstack/react-table";
import {truncateString} from "../../Utils/StringHelper";
import {TextInput} from "../../Components/Form/TextInput";
import {SecondaryButton} from "../../Components/Buttons/SecondaryButton";
import {Link} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowRight, faDownload, faCommentDots} from "@fortawesome/free-solid-svg-icons";
import {TanStackTable} from "../../Components/Tables/TanStackTable";
import {BaseCard} from "../../Components/Cards/BaseCard";
import {useBoundStore} from "../../Store/store";
import {useEffect, useState} from "react";
import dayjs from "dayjs";
import {ScoreDialog} from "../../Components/Dialogs/PerformanceReports/ScoreDialog";
import {RejectDialog} from "../../Components/Dialogs/PerformanceReports/RejectDialog";
import {RemarksDialog} from "../../Components/Dialogs/PerformanceReports/RemarksDialog";
import * as React from "react";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import {DangerButton} from "../../Components/Buttons/DangerButton";
import {AcceptConfirmDialog} from "../../Components/Dialogs/Confirmation/AcceptConfirmDialog";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";

export const MyPerformanceReports = () => {
    const {t} = useTranslation();
    const performanceStatements = useBoundStore((state) => state.performanceStatements)
    const performanceStatementsMetaData = useBoundStore((state) => state.performanceStatementsMetaData)
    const acceptPerformanceStatement = useBoundStore((state) => state.acceptPerformanceStatement)
    const getMyPerformanceStatements = useBoundStore((state) => state.getMyPerformanceStatements)
    const [statusFilter, setStatusFilter] = useState([]);
    const [forceNewData, setForceNewData] = useState(false);

    const [openAcceptPopup, setOpenAcceptPopup] = useState(false);
    const [openRejectReasonPopup, setOpenRejectReasonPopup] = useState(false);
    const [openRemarksDialog, setOpenRemarksDialog] = useState(false);
    const [selectedPerformanceStatement, setSelectedPerformanceStatement] = useState(null);
    const [selectedRemarks, setSelectedRemarks] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);


    const handleFilterClick = (status) => {
        if(statusFilter.includes(status)) {
            setStatusFilter(statusFilter.filter(s => s !== status))
        } else {
            setStatusFilter([...statusFilter, status])
        }
    }

    useEffect(() => {
        setForceNewData(!forceNewData);
    }, [performanceStatements]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter]);

    useEffect(() => {
        getMyPerformanceStatements(statusFilter, currentPage)
    }, [getMyPerformanceStatements, statusFilter, currentPage]);

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('from.formatted', {
            header: t('tables.from'),
            cell: props => dayjs(props.cell.getValue()).format('DD/MM/YYYY'),
        }),
        columnHelper.accessor('branch', {
            header: t('performance_reports.care_partner'),
            cell: props => {
                return truncateString(`${props.cell.getValue()?.company_name ?? '-'}`)
            },
        }),
        columnHelper.accessor('remarks', {
            header: t('tables.remarks'),
            cell: props => {
                const remarks = props.cell.getValue() || [];
                const remarksCount = remarks.length;

                if (remarksCount === 0) {
                    return <p className="text-gray-400">-</p>;
                }

                return (
                    <button
                        onClick={() => {
                            setSelectedRemarks(remarks);
                            setOpenRemarksDialog(true);
                        }}
                        className="relative inline-flex items-center justify-center"
                    >
                        <FontAwesomeIcon
                            icon={faCommentDots}
                            className="text-primary cursor-pointer hover:text-primary-700 transition-colors text-xl"
                        />
                        {remarksCount > 0 && (
                            <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform bg-red-600 rounded-full min-w-[18px]">
                                {remarksCount}
                            </span>
                        )}
                    </button>
                );
            },
        }),
        columnHelper.accessor('document', {
            header: t('tables.document'),
            cell: props => {
                if(props.cell?.getValue() !== null && props.cell.getValue()?.hasOwnProperty('url') ) {
                    return (
                        <div className={'flex flex-row space-x-4'}>
                            <FontAwesomeIcon icon={faDownload} className={'text-primary cursor-pointer'} onClick={() => window.open(props.cell.getValue().url, '_blank')}/>
                        </div>
                    )
                } else {
                    return (<p>-</p>)
                }
            },
        }),
        columnHelper.accessor('status', {
            header: t('tables.status'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        {
                            ['accepted'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_accepted')}</div>
                            )
                        }

                        {
                            ['sent'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-blue-300 text-blue-900 rounded-full text-xs opacity-70'}>{t('performance_reports.status_waiting_for_signers')}</div>
                            )
                        }

                        {
                            ['feedback_required_freelancer'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('performance_reports.waiting_for_your_response')}</div>
                            )
                        }

                        {
                            ['feedback_required_customer'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-gray-300 text-gray-900 rounded-full text-xs opacity-70'}>{t('performance_reports.waiting_for_customer')}</div>
                            )
                        }

                        {
                            ['pending'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('performance_reports.to_confirm')}</div>
                            )
                        }

                        {
                            ['rejected'].includes(props.row.original.status) && (
                                <div className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70'}>{t('general.status.rejected')}</div>
                            )
                        }
                    </div>
                )
            },
        }),
        columnHelper.accessor('actions', {
            header: t('tables.actions'),
            cell: props => {
                const performanceStatement = props.row.original;
                const documensoData = performanceStatement.documenso_data;

                let freelancerSigningUrl = null;
                let freelancerHasSigned = false;
                if (documensoData && documensoData.recipients) {
                    const freelancerRecipient = documensoData.recipients.find(
                        recipient => recipient.email === performanceStatement.freelancer?.email
                    );
                    if (freelancerRecipient) {
                        freelancerSigningUrl = freelancerRecipient.signingUrl;
                        freelancerHasSigned = freelancerRecipient.signingStatus === 'SIGNED';
                    }
                }

                return (
                    <div className={'flex flex-row items-center space-x-4'}>
                        {
                            ['sent', 'pending', 'feedback_required_freelancer'].includes(props.row.original.status) && (
                                <>
                                    {freelancerHasSigned ? (
                                        <div className="flex items-center px-4 py-2 bg-green-50 border border-green-300 rounded-md">
                                            <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-sm font-medium text-green-800">{t('contracts.status_signed')}</span>
                                        </div>
                                    ) : freelancerSigningUrl ? (
                                        <PrimaryButton
                                            fullWidth={false}
                                            onClick={() => {
                                                window.open(freelancerSigningUrl, '_blank');
                                            }}
                                        >
                                            {t('performance_reports.sign')}
                                        </PrimaryButton>
                                    ) : (
                                        <PrimaryButton
                                            fullWidth={false}
                                            onClick={() => {
                                                setOpenAcceptPopup(true)
                                                setSelectedPerformanceStatement(props.row.original.id)
                                            }}
                                        >
                                            {t('general.actions.accept')}
                                        </PrimaryButton>
                                    )}
                                    {!freelancerHasSigned && (
                                        <DangerButton fullWidth={false} onClick={() => {
                                            setOpenRejectReasonPopup(true)
                                            setSelectedPerformanceStatement(props.row.original.id)
                                        }}>
                                            {t('general.actions.reject')}
                                        </DangerButton>
                                    )}
                                </>
                            )
                        }
                    </div>
                )
            },
        })
    ]

    const statusBadge = (status) => {
        const map = {
            accepted: {cls: 'bg-green-300 text-green-900', label: t('performance_reports.status_accepted')},
            sent: {cls: 'bg-blue-300 text-blue-900', label: t('performance_reports.status_waiting_for_signers')},
            pending: {cls: 'bg-orange-300 text-orange-900', label: t('performance_reports.to_confirm')},
            feedback_required_freelancer: {cls: 'bg-orange-300 text-orange-900', label: t('performance_reports.waiting_for_your_response')},
            feedback_required_customer: {cls: 'bg-gray-300 text-gray-900', label: t('performance_reports.waiting_for_customer')},
            rejected: {cls: 'bg-red-300 text-red-900', label: t('general.status.rejected')},
        };
        const info = map[status];
        if (!info) return null;
        return <span className={`px-2 py-1 rounded-full text-xs ${info.cls}`}>{info.label}</span>;
    };

    return (
        <div className="px-4 md:px-0">
            <div className={'mb-2'}>
                <div className={'flex justify-between'}>
                    <div className={'my-auto w-full md:w-1/3'}>
                        <TextInput
                            type={'text'}
                            name={'search'}
                            translationIndex={null}
                            placeholder={t('general.actions.search')}
                            onChange={(e) => console.log(e.target.value)}
                        >
                        </TextInput>
                    </div>
                    <div className={'hidden md:grid grid-cols-3 gap-4 my-auto'}>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('pending,feedback_required_freelancer,feedback_required_customer')} onClick={() => {
                                handleFilterClick('pending,feedback_required_freelancer,feedback_required_customer')
                            }}>
                                {t('performance_reports.to_confirm')}
                            </SecondaryButton>
                        </div>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('accepted')} onClick={() => {
                                handleFilterClick('accepted')
                            }}>
                                {t('performance_reports.status_accepted')}
                            </SecondaryButton>
                        </div>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('rejected')} onClick={() => {
                                handleFilterClick('rejected')
                            }}>
                                {t('general.status.rejected')}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
                <div className={'grid md:hidden grid-cols-3 gap-2 mt-2'}>
                    <SecondaryButton outlined={!statusFilter.includes('pending,feedback_required_freelancer,feedback_required_customer')} onClick={() => handleFilterClick('pending,feedback_required_freelancer,feedback_required_customer')}>
                        {t('performance_reports.to_confirm')}
                    </SecondaryButton>
                    <SecondaryButton outlined={!statusFilter.includes('accepted')} onClick={() => handleFilterClick('accepted')}>
                        {t('performance_reports.status_accepted')}
                    </SecondaryButton>
                    <SecondaryButton outlined={!statusFilter.includes('rejected')} onClick={() => handleFilterClick('rejected')}>
                        {t('general.status.rejected')}
                    </SecondaryButton>
                </div>
            </div>

            {/* Desktop: tabel */}
            <div className="hidden md:block">
                <BaseCard>
                    {performanceStatements?.length === 0 ? (
                        <p data-testid="empty-state" className="text-sm text-gray-500 text-center py-8">{t('performance_reports.no_results')}</p>
                    ) : (
                        <TanStackTable
                            columns={columns}
                            tableData={performanceStatements}
                            forceNewData={forceNewData}
                            laravelPaginationMetaData={performanceStatementsMetaData}
                            onChangePage={(page) => {
                                setCurrentPage(page +1)
                            }}
                        />
                    )}
                </BaseCard>
            </div>

            {/* Mobiel: cards */}
            <div className="md:hidden space-y-3">
                {performanceStatements?.length === 0 && (
                    <p data-testid="empty-state" className="text-sm text-gray-500 text-center py-8">{t('performance_reports.no_results')}</p>
                )}
                {performanceStatements?.map((ps) => (
                    <BaseCard key={ps.id}>
                        <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm text-gray-900 truncate">{ps.branch?.company_name ?? '-'}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {dayjs(ps.from?.formatted).format('DD/MM/YYYY')}
                                </p>
                            </div>
                            {statusBadge(ps.status)}
                        </div>
                        <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                            <div className="text-xs text-gray-500">
                                {ps.remarks?.length > 0 && (
                                    <button
                                        onClick={() => { setSelectedRemarks(ps.remarks); setOpenRemarksDialog(true); }}
                                        className="text-primary font-medium"
                                    >
                                        {ps.remarks.length} {t('tables.remarks').toLowerCase()}
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                {ps.document?.url && (
                                    <button onClick={() => window.open(ps.document.url, '_blank')} className="text-primary text-xs font-medium flex items-center gap-1">
                                        <FontAwesomeIcon icon={faDownload} className="text-xs"/>
                                        {t('tables.document')}
                                    </button>
                                )}
                            </div>
                        </div>
                        {['sent', 'pending', 'feedback_required_freelancer'].includes(ps.status) && (() => {
                            const documensoData = ps.documenso_data;
                            let freelancerSigningUrl = null;
                            let freelancerHasSigned = false;
                            if (documensoData?.recipients) {
                                const freelancerRecipient = documensoData.recipients.find(
                                    r => r.email === ps.freelancer?.email
                                );
                                if (freelancerRecipient) {
                                    freelancerSigningUrl = freelancerRecipient.signingUrl;
                                    freelancerHasSigned = freelancerRecipient.signingStatus === 'SIGNED';
                                }
                            }
                            return (
                                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                                    {freelancerHasSigned ? (
                                        <div className="flex items-center px-3 py-1.5 bg-green-50 border border-green-300 rounded-md w-full justify-center">
                                            <svg className="w-4 h-4 text-green-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-xs font-medium text-green-800">{t('contracts.status_signed')}</span>
                                        </div>
                                    ) : freelancerSigningUrl ? (
                                        <PrimaryButton className="flex-1" onClick={() => window.open(freelancerSigningUrl, '_blank')}>
                                            {t('performance_reports.sign')}
                                        </PrimaryButton>
                                    ) : (
                                        <PrimaryButton className="flex-1" onClick={() => { setOpenAcceptPopup(true); setSelectedPerformanceStatement(ps.id); }}>
                                            {t('general.actions.accept')}
                                        </PrimaryButton>
                                    )}
                                    {!freelancerHasSigned && (
                                        <DangerButton className="flex-1" onClick={() => { setOpenRejectReasonPopup(true); setSelectedPerformanceStatement(ps.id); }}>
                                            {t('general.actions.reject')}
                                        </DangerButton>
                                    )}
                                </div>
                            );
                        })()}
                    </BaseCard>
                ))}
                {performanceStatementsMetaData?.last_page > 1 && (
                    <div className="flex justify-center gap-2 py-2">
                        {Array.from({length: performanceStatementsMetaData.last_page}, (_, i) => (
                            <button
                                key={i}
                                onClick={() => setCurrentPage(i + 1)}
                                className={`px-3 py-1 rounded text-sm ${currentPage === i + 1 ? 'bg-black text-white' : 'bg-gray-100 text-gray-700'}`}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <AcceptConfirmDialog
                open={openAcceptPopup}
                onClose={() => setOpenAcceptPopup(false)}
                performanceStatementId={selectedPerformanceStatement}
                onConfirm={() => {
                    acceptPerformanceStatement(selectedPerformanceStatement)
                        .then(() => {
                            setOpenAcceptPopup(false);
                            toast.success(t('performance_reports.toast_accepted'));
                            getMyPerformanceStatements(statusFilter, currentPage)
                        })
                }}
            />
            <RejectDialog
                isOpen={openRejectReasonPopup}
                onClose={() => setOpenRejectReasonPopup(false)}
                performanceStatementId={selectedPerformanceStatement}
                onSuccess={() => getMyPerformanceStatements(statusFilter, currentPage)}
            />
            <RemarksDialog
                open={openRemarksDialog}
                onClose={() => setOpenRemarksDialog(false)}
                remarks={selectedRemarks}
            />
        </div>
    )
}